import type { SocialActionType, SocialRule } from './types';

/**
 * What each action's form shows, read off the five Figma modals rather than assumed.
 *
 * The variation is real, not cosmetic:
 *  - only Auto-Reply asks what to reply, because it is the only action that posts text;
 *  - Auto-Close has no "close ticket after action" because that is what it already does.
 *
 * A Kim-authored rule (`editable: false`) has a read-only name and condition and no "Save as Draft":
 * the only thing to save is which channels it runs on.
 */
export interface RuleFormConfig {
	title: string;
	/** "When to Close?" — the design words this per action. */
	conditionLabel: string;
	replyText: boolean;
	/**
	 * `'required'` where the tag IS the action. Auto-Flag writes nothing else, so a flag rule with
	 * no tag matches comments, wins on precedence, spends the model calls and then fails at
	 * execution — the brand sees an enabled rule that quietly does nothing. Everywhere else a tag
	 * is a genuine extra alongside the real action.
	 */
	tags: boolean | 'required';
	like: boolean;
	closeAfterAction: boolean;
}

export const RULE_FORM_CONFIG: Record<SocialActionType, RuleFormConfig> = {
	auto_close: {
		title: 'Auto-Close rules',
		conditionLabel: 'When to Close?',
		replyText: false,
		tags: true,
		like: true,
		closeAfterAction: false
	},
	auto_flag: {
		title: 'Auto-Flag rules',
		conditionLabel: 'When to Flag?',
		replyText: false,
		tags: 'required',
		like: false,
		closeAfterAction: true
	},
	auto_reply: {
		title: 'Auto-Reply rules',
		conditionLabel: 'When to reply?',
		replyText: true,
		tags: true,
		like: true,
		closeAfterAction: true
	},
	auto_hide: {
		title: 'Auto-Hide rules',
		conditionLabel: 'When to hide?',
		replyText: false,
		tags: true,
		like: false,
		closeAfterAction: true
	},
	auto_delete: {
		title: 'Auto-Delete rules',
		conditionLabel: 'When to delete?',
		replyText: false,
		tags: false,
		like: false,
		closeAfterAction: false
	}
};

/**
 * Whether the brand may change what this rule MEANS.
 *
 * Taken from the rule when editing (the server computes it and is what enforces it). A new rule is
 * the brand's own, whatever its action.
 */
export const canEditMeaning = (rule: SocialRule | null): boolean => rule?.editable ?? true;
